import React, { useState, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw, 
  X, 
  FileCheck, 
  ShieldCheck, 
  Landmark, 
  Scale, 
  FileSpreadsheet, 
  Award,
  ArrowRight,
  RotateCcw,
  Building2,
  Calendar,
  Hash,
  Info
} from 'lucide-react';
import { ViewerDocument, DocumentFileType } from '../types/documentViewer';

export interface CorporateDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendorProfile: any;
  formData: any;
  onDocumentUploadedAndSynced: (doc: ViewerDocument, extractedData?: any) => void;
}

export type SupportedCorporateDocType = 
  | 'Articles of Incorporation'
  | 'Certificate of Incorporation'
  | 'EIN (IRS Form SS4)'
  | 'Certificate of Good Standing'
  | 'Bylaws & Governance Rules'
  | 'W-9 Request for Taxpayer ID';

export const CorporateDocumentUploadModal: React.FC<CorporateDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  vendorProfile,
  formData,
  onDocumentUploadedAndSynced
}) => {
  const [selectedDocType, setSelectedDocType] = useState<SupportedCorporateDocType>('Articles of Incorporation');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileText, setFileText] = useState<string>('');
  const [customTitle, setCustomTitle] = useState<string>('');
  
  // Inspection & Verification State
  const [isInspecting, setIsInspecting] = useState<boolean>(false);
  const [inspectionResult, setInspectionResult] = useState<{
    isValid: boolean;
    selectedType: string;
    detectedType: string;
    confidenceScore: number;
    inspectionSummary: string;
    rejectionReason?: string | null;
    suggestedRemediation?: string | null;
    extractedData?: any;
    markdownFormattedContent?: string;
  } | null>(null);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (file: File) => {
    setSelectedFile(file);
    setInspectionResult(null);
    setErrorMessage(null);

    // Auto-generate title if empty
    if (!customTitle || customTitle === selectedDocType) {
      const cleanName = file.name.replace(/\.[^/.]+$/, '');
      setCustomTitle(`${selectedDocType} - ${cleanName}`);
    }

    // Read text & base64
    try {
      if (file.type.includes('text') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
        const text = await file.text();
        setFileText(text);
      } else {
        const text = await file.text().catch(() => '');
        setFileText(text);
      }

      const reader = new FileReader();
      reader.onload = () => {
        const res = reader.result as string;
        if (res && res.includes(',')) {
          setFileBase64(res.split(',')[1]);
        } else {
          setFileBase64(res);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.warn("File read error:", err);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDocTypeSelect = (type: SupportedCorporateDocType) => {
    setSelectedDocType(type);
    if (selectedFile) {
      const cleanName = selectedFile.name.replace(/\.[^/.]+$/, '');
      setCustomTitle(`${type} - ${cleanName}`);
    } else {
      setCustomTitle(type);
    }
    // Clear previous inspection to re-evaluate against new selected type
    setInspectionResult(null);
  };

  const handleInspectDocument = async () => {
    if (!selectedFile) {
      setErrorMessage("Please select a corporate document file to upload.");
      return;
    }

    setIsInspecting(true);
    setErrorMessage(null);
    setInspectionResult(null);

    const compName = formData.companyName || vendorProfile?.companyName || 'Corporate Entity';

    try {
      const res = await fetch('/api/inspect-corporate-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedDocumentType: selectedDocType,
          fileName: selectedFile.name,
          mimeType: selectedFile.type || 'application/pdf',
          fileBase64: fileBase64,
          fileText: fileText,
          expectedCompanyName: compName
        })
      });

      if (!res.ok) {
        throw new Error(`AI Document Inspection service returned status ${res.status}`);
      }

      const data = await res.json();
      setInspectionResult(data);
    } catch (err: any) {
      console.error("AI inspection error:", err);
      setErrorMessage(`Inspection failed: ${err.message || 'Unable to connect to AI Document Intelligence service'}`);
    } finally {
      setIsInspecting(false);
    }
  };

  const handleRestartUpload = () => {
    setSelectedFile(null);
    setFileBase64(null);
    setFileText('');
    setInspectionResult(null);
    setErrorMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmSaveAndSync = async () => {
    if (!inspectionResult || !inspectionResult.isValid || !selectedFile) return;

    setIsSaving(true);
    try {
      const ext = selectedFile.name.split('.').pop()?.toLowerCase() || 'pdf';
      let fileType: DocumentFileType = 'pdf';
      if (['docx', 'doc', 'rtf', 'odt', 'txt', 'md'].includes(ext)) {
        fileType = ext === 'md' || ext === 'txt' ? (ext as any) : 'docx';
      } else if (['xlsx', 'xls', 'csv', 'tsv'].includes(ext)) {
        fileType = ext === 'csv' ? 'csv' : 'xlsx';
      } else if (['pptx', 'ppt', 'odp'].includes(ext)) {
        fileType = 'pptx';
      }

      const extracted = inspectionResult.extractedData || {};
      const compName = extracted.companyName || formData.companyName || vendorProfile?.companyName || 'Corporate Entity';
      const fileId = extracted.companyId || extracted.dosId || vendorProfile?.companyId || 'DB288956';
      const incDate = extracted.incorporationDate || extracted.filingDate || vendorProfile?.incorporationDate || '12/13/2024';
      const authority = extracted.issuingAuthority || (selectedDocType.includes('EIN') ? 'Internal Revenue Service (IRS)' : 'Department of State - Division of Corporations');

      const blobUrl = URL.createObjectURL(selectedFile);

      const newDoc: ViewerDocument = {
        id: `corp-doc-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        title: customTitle || `${selectedDocType} - ${compName}`,
        fileType: fileType,
        fileUrl: blobUrl,
        rawContent: fileText || inspectionResult.markdownFormattedContent || '',
        markdownContent: inspectionResult.markdownFormattedContent || `### ${selectedDocType}\n\n**Company Legal Name:** ${compName}\n**Issuing Authority:** ${authority}\n**Filing/Issuance Date:** ${incDate}\n**State File ID / Ref:** ${fileId}\n\n*Verified by AI Document Intelligence on ${new Date().toLocaleDateString()}*`,
        metadata: {
          title: customTitle || `${selectedDocType} - ${compName}`,
          fileName: selectedFile.name,
          fileType: fileType,
          fileSize: `${(selectedFile.size / 1024).toFixed(1)} KB`,
          issuingAuthority: authority,
          filingDate: extracted.filingDate || new Date().toLocaleDateString(),
          incorporationDate: incDate,
          companyName: compName,
          filingNumber: fileId,
          documentType: selectedDocType,
          status: 'AI Verified & Active',
          isSavedToFirestore: true
        }
      };

      // Call parent callback to sync with Firestore and update Vendor Profile state
      onDocumentUploadedAndSynced(newDoc, extracted);
      onClose();
    } catch (err: any) {
      console.error("Save & Sync error:", err);
      setErrorMessage(`Error saving verified document: ${err.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl w-full max-w-2xl text-white flex flex-col max-h-[92vh] overflow-hidden">
        
        {/* Modal Header */}
        <div className="px-6 py-4.5 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/20 border border-indigo-500/40 text-indigo-400 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-white flex items-center gap-2">
                Upload & AI Document Intelligence Inspection
              </h3>
              <p className="text-[11px] text-slate-400">
                Specify document type, inspect authenticity with AI, and sync verified records to Vendor Profile.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Content Scroll Area */}
        <div className="p-6 overflow-y-auto space-y-5 text-xs">
          
          {/* Error Banner */}
          {errorMessage && (
            <div className="p-3 bg-rose-950/80 border border-rose-500/50 rounded-xl text-rose-200 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <p className="leading-relaxed">{errorMessage}</p>
            </div>
          )}

          {/* STEP 1: Select Document Type */}
          <div className="space-y-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 flex items-center justify-between">
              <span>1. Specify Document Type <span className="text-rose-400">*</span></span>
              <span className="text-[10px] text-indigo-400 font-medium">Mandatory Classification</span>
            </label>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { type: 'Articles of Incorporation', icon: Scale, desc: 'Corporate charter & bylaws' },
                { type: 'Certificate of Incorporation', icon: Award, desc: 'State certified formation' },
                { type: 'EIN (IRS Form SS4)', icon: Landmark, desc: 'IRS Federal Tax ID Notice' },
                { type: 'Certificate of Good Standing', icon: ShieldCheck, desc: 'Active statutory standing' },
                { type: 'Bylaws & Governance Rules', icon: FileCheck, desc: 'Internal operational rules' },
                { type: 'W-9 Request for Taxpayer ID', icon: FileText, desc: 'Tax certification form' }
              ].map((item) => {
                const isSelected = selectedDocType === item.type;
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.type}
                    type="button"
                    onClick={() => handleDocTypeSelect(item.type as SupportedCorporateDocType)}
                    className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-1 ${
                      isSelected 
                        ? 'bg-indigo-600/20 border-indigo-500 text-white ring-1 ring-indigo-500' 
                        : 'bg-slate-800/60 border-slate-700/80 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <IconComponent className={`w-4 h-4 ${isSelected ? 'text-indigo-400' : 'text-slate-400'}`} />
                      {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400" />}
                    </div>
                    <div>
                      <p className="font-bold text-[11px] leading-tight">{item.type}</p>
                      <p className="text-[9px] text-slate-400 truncate">{item.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 2: File Upload Zone */}
          <div className="space-y-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300">
              2. Upload File (PDF, Scanned Image, DOCX, TXT) <span className="text-rose-400">*</span>
            </label>

            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`p-6 border-2 border-dashed rounded-2xl text-center cursor-pointer transition-all ${
                selectedFile 
                  ? 'border-indigo-500/80 bg-indigo-950/20' 
                  : 'border-slate-700 hover:border-indigo-500 bg-slate-800/40 hover:bg-slate-800/60'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.txt,.md"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChange(file);
                }}
              />

              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600/30 text-indigo-400 border border-indigo-500/40 flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-white text-xs">{selectedFile.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {(selectedFile.size / 1024).toFixed(1)} KB • Ready for AI Document Intelligence Inspection
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestartUpload();
                    }}
                    className="ml-auto p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg text-xs font-semibold cursor-pointer"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 text-slate-400 flex items-center justify-center mx-auto">
                    <Upload className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-200">
                      Click to browse or drag & drop corporate document
                    </p>
                    <p className="text-[10px] text-slate-400">
                      Supports PDF, PNG, JPG, Word (.docx), and Markdown/Text files
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Document Label / Title */}
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Document Label / Record Name
            </label>
            <input
              type="text"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 outline-none"
              placeholder={`e.g. ${selectedDocType} - Quantum AI Inc`}
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
            />
          </div>

          {/* Inspection In Progress Animation */}
          {isInspecting && (
            <div className="p-5 bg-indigo-950/50 border border-indigo-500/40 rounded-2xl space-y-3 animate-pulse">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
                <div>
                  <h4 className="font-bold text-xs text-indigo-200">
                    AI Document Intelligence Inspection in Progress...
                  </h4>
                  <p className="text-[10px] text-indigo-300">
                    Scanning statutory seals, legal structure, agency letterhead, and verifying alignment with "{selectedDocType}"...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* INSPECTION RESULTS FEEDBACK */}
          {inspectionResult && (
            <div className="space-y-3 animate-fade-in">
              {inspectionResult.isValid ? (
                /* SUCCESS RESULT */
                <div className="p-5 bg-emerald-950/70 border border-emerald-500/50 rounded-2xl space-y-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-400/30 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-xs text-emerald-200">
                          AI Inspection Passed: Verified {inspectionResult.detectedType}
                        </h4>
                        <p className="text-[10px] text-emerald-300">
                          {inspectionResult.inspectionSummary}
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold">
                      {inspectionResult.confidenceScore}% Confidence
                    </span>
                  </div>

                  {/* Extracted Entity Badges */}
                  {inspectionResult.extractedData && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-emerald-500/30 text-[10px]">
                      {inspectionResult.extractedData.companyName && (
                        <div className="bg-emerald-900/40 p-2 rounded-lg">
                          <span className="text-emerald-400/80 block font-semibold">Entity Name:</span>
                          <span className="font-bold text-white truncate block">{inspectionResult.extractedData.companyName}</span>
                        </div>
                      )}
                      {inspectionResult.extractedData.ein && (
                        <div className="bg-emerald-900/40 p-2 rounded-lg">
                          <span className="text-emerald-400/80 block font-semibold">EIN Tax ID:</span>
                          <span className="font-mono font-bold text-white block">{inspectionResult.extractedData.ein}</span>
                        </div>
                      )}
                      {inspectionResult.extractedData.companyId && (
                        <div className="bg-emerald-900/40 p-2 rounded-lg">
                          <span className="text-emerald-400/80 block font-semibold">Company ID:</span>
                          <span className="font-mono font-bold text-white block">{inspectionResult.extractedData.companyId}</span>
                        </div>
                      )}
                      {inspectionResult.extractedData.incorporationDate && (
                        <div className="bg-emerald-900/40 p-2 rounded-lg">
                          <span className="text-emerald-400/80 block font-semibold">Incorporation Date:</span>
                          <span className="font-semibold text-white block">{inspectionResult.extractedData.incorporationDate}</span>
                        </div>
                      )}
                      {inspectionResult.extractedData.issuingAuthority && (
                        <div className="bg-emerald-900/40 p-2 rounded-lg col-span-2">
                          <span className="text-emerald-400/80 block font-semibold">Issuing Authority:</span>
                          <span className="font-semibold text-white truncate block">{inspectionResult.extractedData.issuingAuthority}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* REJECTION RESULT WITH RESTART DIALOGUE */
                <div className="p-5 bg-rose-950/80 border border-rose-500/60 rounded-2xl space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-400/30 flex items-center justify-center shrink-0">
                      <AlertCircle className="w-5 h-5" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-bold text-xs text-rose-200 flex items-center gap-2">
                        Document Inspection Failed — Document Rejected
                      </h4>
                      <p className="text-[11px] text-rose-300 leading-relaxed">
                        {inspectionResult.rejectionReason || `The uploaded document does not match the mandatory statutory criteria for "${selectedDocType}".`}
                      </p>
                    </div>
                  </div>

                  <div className="bg-rose-900/40 border border-rose-700/50 p-3 rounded-xl space-y-1.5 text-[11px]">
                    <div className="flex justify-between text-rose-200">
                      <span className="text-rose-400">Selected Type:</span>
                      <span className="font-bold">{selectedDocType}</span>
                    </div>
                    <div className="flex justify-between text-rose-200">
                      <span className="text-rose-400">Detected Document Type:</span>
                      <span className="font-bold text-amber-300">{inspectionResult.detectedType || 'Unrecognized / Non-matching'}</span>
                    </div>
                    {inspectionResult.suggestedRemediation && (
                      <div className="pt-1.5 border-t border-rose-700/40 text-rose-200/90 text-[10px]">
                        <strong>Guidance:</strong> {inspectionResult.suggestedRemediation}
                      </div>
                    )}
                  </div>

                  <div className="pt-2 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={handleRestartUpload}
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-md shadow-rose-950"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Restart Document Upload & Select Correct File</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Modal Footer Controls */}
        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>

          <div className="flex items-center gap-2.5">
            {!inspectionResult?.isValid ? (
              <button
                type="button"
                onClick={handleInspectDocument}
                disabled={!selectedFile || isInspecting}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:cursor-not-allowed"
              >
                {isInspecting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Inspecting Document...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5 text-indigo-300" />
                    <span>Inspect with AI Document Intelligence</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConfirmSaveAndSync}
                disabled={isSaving}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Saving to Profile...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Save & Sync to Vendor Profile</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
